import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  deleteSchedule,
  getSchedule,
  listScheduleRuns,
  runScheduleNow,
  toggleSchedule,
} from '../../services/schedulerApi';
import {
  ConfirmModal,
  RelTime,
  Skeleton,
  StatusBadge,
  Toast,
  Toggle,
} from '../components';
import {
  JOB_TYPES,
  absoluteTime,
  cadenceText,
  durationText,
  isPushJobType,
  relativeTime,
  rowsProcessed,
  targetChipLabel,
  targetKey,
} from '../format';

const POLL_MS = 10000;

export default function ScheduleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [schedule, setSchedule] = useState(null);
  const [runs, setRuns] = useState(null);
  const [expandedError, setExpandedError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, error = false) => {
    setToast({ message, error });
    window.setTimeout(() => setToast(null), 3200);
  }, []);

  const load = useCallback(async (silent = false) => {
    try {
      const [s, r] = await Promise.all([
        getSchedule(id),
        listScheduleRuns(id, { limit: 50 }),
      ]);
      setSchedule(s);
      setRuns(r.runs);
    } catch (e) {
      if (!silent) showToast(e?.response?.data?.detail || 'Could not load schedule', true);
    }
  }, [id, showToast]);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const handleToggle = async (enabled) => {
    setSchedule((s) => ({ ...s, enabled }));
    try {
      const updated = await toggleSchedule(id, enabled);
      setSchedule((s) => ({ ...s, ...updated }));
    } catch (e) {
      setSchedule((s) => ({ ...s, enabled: !enabled }));
      showToast(e?.response?.data?.detail || 'Could not update schedule', true);
    }
  };

  const handleRunNow = async () => {
    try {
      await runScheduleNow(id);
      showToast('Run queued');
      load(true);
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Could not start run', true);
    }
  };

  const handleDelete = async () => {
    setDeleteBusy(true);
    try {
      await deleteSchedule(id);
      navigate('/jobs/schedules');
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Delete failed', true);
      setDeleteBusy(false);
    }
  };

  if (!schedule) {
    return (
      <div>
        <Link to="/jobs/schedules" className="sched-backlink">← Back to scheduler</Link>
        <Skeleton height={110} style={{ marginBottom: 16 }} />
        <Skeleton height={300} />
      </div>
    );
  }

  const lastFailedRun = runs?.find((r) => r.status === 'failed');
  const showFailureBanner = schedule.last_status === 'failed' && lastFailedRun;
  const running = schedule.last_status === 'running' || schedule.last_status === 'pending';

  return (
    <div>
      <Link to="/jobs/schedules" className="sched-backlink">← Back to scheduler</Link>

      <div className="sched-detail-head">
        <div>
          <h1>{schedule.name}</h1>
          <div className="sched-detail-meta">
            <span className="sched-chip">{JOB_TYPES[schedule.job_type]?.label || schedule.job_type}</span>
            {isPushJobType(schedule.job_type)
              ? (schedule.targets || []).map((t) => (
                <span
                  key={targetKey(t)}
                  className={`sched-chip sched-chip--co${t.environment === 'staging' ? ' sched-chip--staging' : ''}`}
                >
                  {targetChipLabel(t)}
                </span>
              ))
              : (schedule.companies || []).map((c) => (
                <span key={c} className="sched-chip sched-chip--co">{c}</span>
              ))}
            {schedule.job_type !== 'bc_products' && schedule.job_type !== 'bc_pricing' && (
              <span className="sched-chip">{schedule.sync_mode === 'incremental' ? 'Incremental' : 'Full'}</span>
            )}
            <span>{cadenceText(schedule)}</span>
            {schedule.enabled && schedule.next_run_at && (
              <span title={absoluteTime(schedule.next_run_at)}>
                · next run {relativeTime(schedule.next_run_at)}
              </span>
            )}
            {!schedule.enabled && <span>· paused</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Toggle checked={schedule.enabled} onChange={handleToggle} label="Enable schedule" />
          <button
            type="button"
            className="sched-btn"
            onClick={handleRunNow}
            disabled={running}
            title={running ? 'A run is already queued or in progress' : undefined}
          >
            Run now
          </button>
          <button type="button" className="sched-btn" onClick={() => navigate(`/jobs/schedules/${id}/edit`)}>
            Edit
          </button>
          <button type="button" className="sched-btn sched-btn--danger" onClick={() => setConfirmDelete(true)}>
            Delete
          </button>
        </div>
      </div>

      {showFailureBanner && (
        <div className="sched-banner sched-banner--error">
          <div>
            <strong>Last run failed</strong>{' '}
            <RelTime iso={lastFailedRun.enqueued_at} className="" />
            <code>{lastFailedRun.error || 'No error detail recorded.'}</code>
          </div>
        </div>
      )}

      <div className="sched-table-wrap">
        <table className="sched-table">
          <thead>
            <tr>
              <th>Started</th>
              <th>Status</th>
              <th className="num">Duration</th>
              <th className="num">Rows</th>
              <th>Triggered by</th>
              <th>Job</th>
            </tr>
          </thead>
          <tbody>
            {runs === null ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--sched-muted)' }}>Loading…</td></tr>
            ) : !runs.length ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', color: 'var(--sched-muted)', padding: 32 }}>
                  No runs yet. The scheduler will fire automatically
                  {schedule.enabled ? '' : ' once this schedule is enabled'}, or use Run now.
                </td>
              </tr>
            ) : (
              runs.map((r) => (
                <React.Fragment key={r.id}>
                  <tr>
                    <td title={absoluteTime(r.enqueued_at)}>{relativeTime(r.enqueued_at)}</td>
                    <td>
                      <StatusBadge status={r.status} />
                      {r.status === 'failed' && r.error && (
                        <>
                          {' '}
                          <button
                            type="button"
                            className="sched-error-toggle"
                            onClick={() => setExpandedError(expandedError === r.id ? null : r.id)}
                          >
                            {expandedError === r.id ? 'hide error' : 'show error'}
                          </button>
                        </>
                      )}
                    </td>
                    <td className="num">{durationText(r.job_started_at, r.job_finished_at)}</td>
                    <td className="num">{rowsProcessed(r.job_counters) ?? '—'}</td>
                    <td>{r.job_created_by === 'system:scheduler' ? 'Scheduler' : (r.job_created_by || '—')}</td>
                    <td>
                      {r.job_id
                        ? <Link to={`/jobs/run/epicor`} title={`Epicor job #${r.job_id}`}>#{r.job_id}</Link>
                        : r.bc_job_id
                          ? <Link to={`/jobs/history/${r.bc_job_id}`} title={`BigCommerce job #${r.bc_job_id}`}>#{r.bc_job_id}</Link>
                          : '—'}
                      {r.target && (
                        <span
                          className={`sched-chip sched-chip--co${r.target.environment === 'staging' ? ' sched-chip--staging' : ''}`}
                          style={{ marginLeft: 6 }}
                        >
                          {targetChipLabel(r.target)}
                        </span>
                      )}
                    </td>
                  </tr>
                  {expandedError === r.id && (
                    <tr className="sched-run-error-row">
                      <td colSpan={6}><code>{r.error}</code></td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {confirmDelete && (
        <ConfirmModal
          title={`Delete "${schedule.name}"?`}
          body="The schedule and its run history will be removed. Completed jobs are unaffected. This cannot be undone."
          confirmLabel="Delete schedule"
          danger
          busy={deleteBusy}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
      <Toast message={toast?.message} error={toast?.error} />
    </div>
  );
}
